import { useState } from "react";
import { FileText, ChevronDown, ChevronRight, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { getValidationStatusIcon, getValidationStatusColor } from "../../../utils/validationUtils.js";

export default function FinePrintValidation({ validation }) {
    const [expandedSections, setExpandedSections] = useState({
        matches: false,
        missing: false,
        extra: false,
        bottomContainer: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const matchCount = validation.offerFinePrintMatches?.length || 0;
    const missingCount = validation.missingFinePrint?.length || 0;
    const extraCount = validation.extraFinePrint?.length || 0;
    const hasBottomContainer = validation.bottomContainerValidation;

    return (
        <div className="border-l-4 border-l-purple-500 border-t border-r border-b border-slate-200 rounded-lg overflow-hidden">
            {/* Compact Header with Summary */}
            <div className="bg-purple-50/30 p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileText size={16} className="text-indigo-600" />
                        <h5 className="font-medium text-slate-900">Fine Print Validation</h5>
                        {getValidationStatusIcon(validation.status, validation.score)}
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Quick Stats Badges */}
                        <div className="flex items-center gap-2 text-xs">
                            {matchCount > 0 && (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                    ✓ {matchCount} matched
                                </span>
                            )}
                            {missingCount > 0 && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                                    ✗ {missingCount} missing
                                </span>
                            )}
                            {extraCount > 0 && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                                    ⚠ {extraCount} extra
                                </span>
                            )}
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getValidationStatusColor(validation.status, validation.score)}`}>
                            {validation.score}%
                        </div>
                    </div>
                </div>
            </div>

            {/* Collapsible Content Sections */}
            <div className="divide-y divide-slate-100">
                {/* Missing Fine Print - Most Important */}
                {missingCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('missing')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-red-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.missing ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <AlertCircle size={14} className="text-red-600" />
                                <span className="text-sm font-medium text-red-700">Missing Fine Print</span>
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{missingCount}</span>
                            </div>
                        </button>
                        {expandedSections.missing && (
                            <div className="px-3 pb-3 space-y-2 bg-red-50/30">
                                {validation.missingFinePrint.map((missing, i) => (
                                    <div key={i} className="text-xs bg-white p-2 rounded border border-red-200">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-slate-900 truncate" title={missing.offer}>
                                                    {missing.offer}
                                                </div>
                                                <div className="text-slate-500 mt-0.5">Ref: {missing.footnoteRef}</div>
                                            </div>
                                        </div>
                                        <details className="mt-1">
                                            <summary className="text-slate-600 cursor-pointer hover:text-slate-900">
                                                Expected text...
                                            </summary>
                                            <div className="mt-1 p-2 bg-slate-50 rounded text-slate-600">
                                                {missing.expectedFinePrint}
                                            </div>
                                        </details>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Matched Fine Print */}
                {matchCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('matches')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-emerald-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.matches ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <CheckCircle size={14} className="text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-700">Matched Fine Print</span>
                                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{matchCount}</span>
                            </div>
                        </button>
                        {expandedSections.matches && (
                            <div className="px-3 pb-3 space-y-2 bg-emerald-50/30">
                                {validation.offerFinePrintMatches.map((match, i) => (
                                    <div key={i} className="text-xs bg-white p-2 rounded border border-emerald-200">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-slate-900 truncate" title={match.offer}>
                                                    {match.offer}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-slate-500">Ref: {match.footnoteRef}</span>
                                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                        match.match?.isValid
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                        {match.match?.similarity}% match
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <details className="mt-1">
                                            <summary className="text-slate-600 cursor-pointer hover:text-slate-900">
                                                View found text...
                                            </summary>
                                            <div className="mt-1 p-2 bg-slate-50 rounded text-slate-600">
                                                {match.foundFinePrint}
                                            </div>
                                        </details>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Extra Fine Print */}
                {extraCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('extra')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.extra ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <AlertTriangle size={14} className="text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Extra Fine Print</span>
                                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{extraCount}</span>
                            </div>
                        </button>
                        {expandedSections.extra && (
                            <div className="px-3 pb-3 space-y-2 bg-amber-50/30">
                                {validation.extraFinePrint.map((extra, i) => (
                                    <div key={i} className="text-xs bg-white p-2 rounded border border-amber-200">
                                        <div className="font-medium text-slate-900">Ref: {extra.footnoteRef}</div>
                                        <div className="text-slate-600 mt-1">{extra.finePrintText}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Bottom Container Validation */}
                {hasBottomContainer && (
                    <div>
                        <button
                            onClick={() => toggleSection('bottomContainer')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-purple-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.bottomContainer ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <FileText size={14} className="text-purple-600" />
                                <span className="text-sm font-medium text-purple-700">Bottom Container Check</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    validation.bottomContainerValidation.status === 'error'
                                        ? 'bg-red-100 text-red-700'
                                        : validation.bottomContainerValidation.status === 'warning'
                                            ? 'bg-amber-100 text-amber-700'
                                            : validation.bottomContainerValidation.status === 'success'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-slate-100 text-slate-700'
                                }`}>
                                    {validation.bottomContainerValidation.status}
                                </span>
                            </div>
                        </button>
                        {expandedSections.bottomContainer && (
                            <div className="px-3 pb-3 space-y-2 bg-purple-50/30">
                                <div className="text-xs bg-white p-2 rounded border border-purple-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-600">Container Found:</span>
                                        <span className={`font-medium ${
                                            validation.bottomContainerValidation.bottomContainerFound
                                                ? 'text-emerald-700'
                                                : 'text-red-700'
                                        }`}>
                                            {validation.bottomContainerValidation.bottomContainerFound ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                    {validation.bottomContainerValidation.message && (
                                        <div className="mt-2 text-slate-600 p-2 bg-slate-50 rounded">
                                            {validation.bottomContainerValidation.message}
                                        </div>
                                    )}
                                </div>

                                {/* Footnote Cross-Reference - Compact Grid */}
                                {validation.bottomContainerValidation.footnotesCrossReference?.length > 0 && (
                                    <div className="space-y-1">
                                        <div className="text-xs font-medium text-purple-900 px-2">Footnote Status:</div>
                                        <div className="grid grid-cols-2 gap-1">
                                            {validation.bottomContainerValidation.footnotesCrossReference.map((ref, i) => (
                                                <div
                                                    key={i}
                                                    className={`text-xs p-2 rounded flex items-center justify-between ${
                                                        ref.inBottomContainer
                                                            ? 'bg-emerald-50 border border-emerald-200'
                                                            : 'bg-red-50 border border-red-200'
                                                    }`}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium truncate">{ref.footnote}</div>
                                                        <div className="text-slate-500">
                                                            {ref.offerCount} offer{ref.offerCount !== 1 ? 's' : ''}
                                                        </div>
                                                    </div>
                                                    <div className={`ml-2 text-lg ${
                                                        ref.inBottomContainer ? 'text-emerald-600' : 'text-red-600'
                                                    }`}>
                                                        {ref.inBottomContainer ? '✓' : '✗'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Bottom Container Text - Collapsed by default */}
                                {validation.bottomContainerValidation.bottomContainerText && (
                                    <details className="text-xs">
                                        <summary className="cursor-pointer hover:text-purple-700 text-purple-900 font-medium px-2 py-1">
                                            View Container Text ({validation.bottomContainerValidation.bottomContainerText.length} chars)
                                        </summary>
                                        <div className="mt-1 p-2 bg-white border border-purple-100 rounded text-slate-600 max-h-32 overflow-y-auto">
                                            {validation.bottomContainerValidation.bottomContainerText}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Empty State */}
            {matchCount === 0 && missingCount === 0 && extraCount === 0 && !hasBottomContainer && (
                <div className="p-4 text-center text-sm text-slate-500">
                    No fine print validation data available
                </div>
            )}
        </div>
    );
}
